// ============================================================================
// RHSolicitacoes_Modelos.js
// Publicação dos MODELOS em branco — os formulários que a loja imprime.
// Só RH/Admin. Renderizada pelo sub-hub RHSolicitacoes.
//
// ⚠️ POR QUE ESTA TELA EXISTE (em vez de um arquivo em public/): é ela que deixa
//    o RH trocar o documento oficial SEM DEPLOY, e é o que garante que a rede
//    imprima sempre a versão vigente. Modelo velho circulando pelas lojas é o
//    modo de falha silencioso deste módulo — ninguém reclama, o RH só recebe
//    papel fora do padrão meses a fio.
//
// ⚠️ Publicar NÃO apaga a versão anterior: desativa. Documento assinado em
//    agosto foi assinado na versão de agosto, e ela continua consultável.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Download, Upload } from 'lucide-react';
import { estilos } from '../../processoseletivo/psUI';
import { baixarArquivoComToken } from '../../../utils/arquivoComToken';

const COR = '#e11d48';
const ACEITA = 'application/pdf,image/jpeg,image/png,image/webp';

function RHSolicitacoesModelos({ tipos = [], aoPublicar }) {
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [enviando, setEnviando] = useState(null);

  // Só os tipos de papel: o Cancelamento de Contrato é tela e não tem modelo.
  const tiposDocumento = tipos.filter((t) => t.formato === 'documento');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch('/api/rh/solicitacoes/modelos', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Não foi possível carregar os modelos.');
      setHistorico(await resp.json());
    } catch (e) {
      setFeedback({ tipo: 'erro', msg: e.message });
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const publicar = async (chave, arquivo) => {
    if (!arquivo) return;
    setEnviando(chave);
    setFeedback(null);
    try {
      const corpo = new FormData();
      corpo.append('tipo', chave);
      corpo.append('arquivo', arquivo);
      const token = localStorage.getItem('authToken');
      const resp = await fetch('/api/rh/solicitacoes/modelos', {
        method: 'POST',
        // ⚠️ SEM Content-Type: o browser monta o boundary do multipart.
        headers: { Authorization: `Bearer ${token}` },
        body: corpo,
      });
      const dados = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(dados.message || 'Erro ao publicar o modelo.');
      setFeedback({ tipo: 'ok', msg: `Modelo publicado (versão ${dados.versao}).` });
      await carregar();
      if (aoPublicar) aoPublicar();   // atualiza o "temModelo" dos cards do sub-hub
    } catch (e) {
      setFeedback({ tipo: 'erro', msg: e.message });
    } finally {
      setEnviando(null);
    }
  };

  const versaoAtiva = (chave) => historico.find((h) => h.TIPO === chave && h.ATIVO === 1) || null;

  return (
    <div>
      <h2 style={estilos.tituloSecao}>Modelos das solicitações</h2>
      <p style={estilos.subtituloSecao}>
        O formulário em branco que a loja baixa para imprimir. Publicar uma versão nova
        substitui a vigente — a anterior fica no histórico.
      </p>

      {feedback && (
        <div style={{
          marginTop: 18, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
          borderRadius: 10, fontSize: 14,
          background: feedback.tipo === 'ok' ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${feedback.tipo === 'ok' ? '#a7f3d0' : '#fecaca'}`,
          color: feedback.tipo === 'ok' ? '#047857' : '#b91c1c',
        }}>
          {feedback.tipo === 'ok' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          {feedback.msg}
        </div>
      )}

      {carregando ? (
        <p style={{ color: '#64748b', fontSize: 14, marginTop: 20 }}>Carregando…</p>
      ) : (
        <div style={{ ...estilos.tabelaWrap, marginTop: 20 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={estilos.tabela}>
              <thead>
                <tr>
                  <th style={estilos.th}>Solicitação</th>
                  <th style={estilos.th}>Versão vigente</th>
                  <th style={estilos.th}>Publicada por</th>
                  <th style={estilos.th}>Quando</th>
                  <th style={estilos.th}></th>
                </tr>
              </thead>
              <tbody>
                {tiposDocumento.map((t) => {
                  const ativa = versaoAtiva(t.chave);
                  return (
                    <tr key={t.chave}>
                      <td style={estilos.td}>{t.rotulo}</td>
                      <td style={estilos.td}>
                        {ativa
                          ? <strong>v{ativa.VERSAO}</strong>
                          : <span style={{ color: '#b45309', fontWeight: 700 }}>sem modelo</span>}
                      </td>
                      <td style={estilos.td}>{ativa ? (ativa.PUBLICADO_POR_NOME || '—') : '—'}</td>
                      <td style={estilos.td}>{ativa ? (ativa.dataPublicacao || '—') : '—'}</td>
                      <td style={estilos.td}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {ativa && (
                            <button
                              onClick={() => baixarArquivoComToken(
                                `/api/rh/solicitacoes/modelos/${t.chave}/arquivo`, `${t.rotulo}.pdf`
                              ).catch((e) => setFeedback({ tipo: 'erro', msg: e.message }))}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                                borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff',
                                color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              }}
                            >
                              <Download size={14} /> Baixar
                            </button>
                          )}
                          <label
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                              borderRadius: 6, border: 'none', background: COR, color: '#fff',
                              fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            }}
                          >
                            <Upload size={14} />
                            {enviando === t.chave ? 'Publicando…' : (ativa ? 'Nova versão' : 'Publicar')}
                            <input
                              type="file"
                              accept={ACEITA}
                              onChange={(e) => {
                                publicar(t.chave, e.target.files && e.target.files[0]);
                                e.target.value = '';
                              }}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default RHSolicitacoesModelos;
